'use client'
import React, { useEffect, useMemo, useState } from 'react';
import { VStack, HStack, Box, Text, Button, Spacer } from '@chakra-ui/react';
import { useToast } from '@mybridge/ui';
import { useMutation } from '@tanstack/react-query';
import { GoogleMeetSuggestion } from '@mybridge/icons/GoogleMeetSuggestion';
import { GoogleMeetIcon } from '@mybridge/icons/GoogleMeetIcon';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Stack,
} from '@mybridge/ui';
import { useDispatch, useSelector } from 'react-redux';
import API from 'v4/store/common/api';
import { getCalendarIntegrationURL } from 'v4/lib/api/google-integration';
import { useRouter } from 'next/router';
import { getGoogleIntegrationCallback, getZoomIntegrationCallback } from 'v4/store/slices/settingsSlice';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';


// Main Google Meet Integration Component
const GoogleMeetIntegration = ({ calendars, setCalendars }) => {
  const { t } = useTranslator();
  const [selectedCalendars, setSelectedCalendars] = useState([]);
  const { confrenceList } = useSelector((state) => state.settings);
  const dispatch = useDispatch();
  const router = useRouter();
  const toast = useToast();
  const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] = useState({
    isOpen: false,
  });

  // Handle calendar removal
  const handleRemoveCalendar = async (selectedAccount) => {

    // return
    try {
      const response = await API({
        url: `api/v1/conference/${selectedAccount?.id}`,
        method: 'DELETE',
      });

      if (response?.message === 'appointment deleted successfully') {
        toast({
          title: t('settingsIntegration.removeCalendarSuccess') || 'Calendar removed successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setCalendars((prevCalendars) =>
          prevCalendars.map((calendar) =>
            calendar.id === selectedAccount?.id
              ? { ...calendar, isConnected: false }
              : calendar
          )
        );
      } else {
        throw new Error('Failed to remove');
      }
    } catch (error) {
      toast({
        title: t('settingsIntegration.errorRemoving') || 'Error removing calendar.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsConfirmRemoveModalOpen({ isOpen: false });
    }
  };

  // Update selected calendars based on conference list from Redux
  useEffect(() => {
    if (confrenceList?.length > 0) {
      const updatedSelected = confrenceList
        .map((cal) => {
          const calendar = calendars?.find((calendar) =>{
            return cal?.provider?.toLowerCase()?.includes('google')}
          );
          if (calendar) {
            return { ...calendar, email: cal?.calendar_id, id: cal?.id };
          }
          return null;
        })
        .filter(Boolean);
      setSelectedCalendars(updatedSelected);
    }
  }, [confrenceList, calendars]);

  // Find Google Meet calendar from the list
  const googleMeetCalendar = useMemo(
    () => calendars?.find((calendar) => calendar?.key === 'google'),
    [calendars]
  );

  // Handle Google integration callback
  useEffect(() => {
    if (typeof window !== 'undefined' && router?.query?.code&&router?.query?.scope) { // Ensure code runs only on client-side
      const payload = {
        state: router?.query?.state,
        scope: router?.query?.scope,
        code: router?.query?.code,
        prompt: router?.query?.prompt,
        authuser: router?.query?.authuser,
        hd: router?.query?.hd,
      };
      dispatch(getGoogleIntegrationCallback(payload)).then((res) => {
        toast({
          title: 'Success',
          description: res?.payload?.data,
          status: 'success',
          duration: 9000,
          isClosable: true,
        });
        router.push('/settings/integration?status=success');
      });
    }

    if (typeof window !== 'undefined' && router?.query?.code&&!router?.query?.scope) { // Ensure code runs only on client-side
      const payload = {
        state: router?.query?.state,
        code: router?.query?.code,
        prompt: router?.query?.prompt,
        authuser: router?.query?.authuser,
        hd: router?.query?.hd,
      };
      dispatch(getZoomIntegrationCallback(payload)).then((res) => {
        toast({
          title: 'Success',
          description: res?.payload?.data,
          status: 'success',
          duration: 9000,
          isClosable: true,
        });
        router.push('/settings/integration?status=success');
      });
    }
  }, [router, dispatch, toast]);
  const { mutate, isLoading, error } = useMutation(getCalendarIntegrationURL, {
    onSuccess: (data) => {
      if (data?.error) {
        alert(data?.error);
      } else {
        const { url = null } = data;
        if (url) {
          router?.push(url); // Use router push to redirect to the URL
        }
      }
    },
    onError: (error) => {
      console.error(error);
    },
  });
  return (
    <>
      {googleMeetCalendar?.isConnected ? (
        // <GoogleMeetConnected
        //   setIsConfirmRemoveModalOpen={setIsConfirmRemoveModalOpen}
        //   selectedCalendar={selectedCalendars[0]}
        // />


        <VStack
        spacing={6}
        align="start"
        p={6}
        mt={6}
        w="100%"
        maxW="100%"
        borderRadius="4px"
        boxShadow="0px 0px 12px 0px #00000033"
        backgroundColor="white"
        fontFamily="Inter"
      >
        <VStack
          w="100%"
          p={4}
          alignItems="start"
          borderRadius="4px"
          border="1px solid #D9D9D9"
          backgroundColor="#FFFFFF"
          spacing={4}
        >
          <Text fontSize="18px" fontWeight="400" color="#747474">
            {t('settingsIntegration.congratulations') || "Congratulations! You can already schedule with Google Meet conferencing because your Google Calendar is connected with myBridge."}
          </Text>
          <HStack w="100%" alignItems="center">
            <GoogleMeetIcon />
            <VStack w="80%" gap={0} h={46} alignItems="flex-start">
              <Text fontSize="12px" fontWeight="400" color="#37ABE7">
                {t('settingsIntegration.connectedBy') || "CONNECTED BY"}
              </Text>
              <Text fontSize="22px" fontWeight="500" color="#5B5B5B">
                {selectedCalendars?.[0]?.email.length>30? selectedCalendars?.[0]?.email.slice(0,30)+'...':selectedCalendars?.[0]?.email  || t('settingsIntegration.noEmailFound') || 'No email found'}
              </Text>
              <Spacer />
            </VStack>
            <Button
              borderRadius="5px"
              fontWeight={600}
              fontSize="14px"
              color="#3D5A80"
              variant="outline"
              size="sm"
              onClick={() => setIsConfirmRemoveModalOpen({ isOpen: true })}
            >
              {t('settingsIntegration.disconnect') || "Disconnect"}
            </Button>
          </HStack>
        </VStack>
    
        <VStack
          w="100%"
          p={4}
          maxW="100%"
          alignItems="start"
          borderRadius="4px"
          border="1px solid #D9D9D9"
          backgroundColor="#FFFFFF"
          spacing={4}
        >
          <Text fontSize="14px" fontWeight="500" color="#5B5B5B">
            {t('settingsIntegration.connectedCalendarFeatures') || "With your connected Google Calendar you can:"}
          </Text>
          <HStack w="100%" p="16px" justifyContent="space-between">
            <GoogleMeetSuggestion />
            <Box width="50%">
              <Text fontSize="14px" fontWeight="500" color="#5B5B5B">
                {t('settingsIntegration.setDefaultLocation') || "Set a default location for event types."}
              </Text>
              <Text fontSize="12px" color="#747474" mt={2}>
                {t('settingsIntegration.googleMeetDefaultLocation') || "Select Google Meet as a default location for your event types to have meeting details automatically added to calendar events."}
              </Text>
            </Box>
          </HStack>
        </VStack>
      </VStack>
      ) : (
         <VStack
      spacing={4}
      align="start"
      p="16px 0 30px 16px"
      mt={6}
      maxW="100%"
      borderRadius="20px"
      boxShadow="0px 0px 12px 0px #00000033"
      backgroundColor="white"
      fontFamily="Inter"
    >
      <Text fontSize="18px" fontWeight="400" color="#5B5B5B">
        {t('myCalendar.googleMeet') || "Google Meet"}
      </Text>
      <HStack w={"100%"} alignItems="flex-start">
        <VStack spacing={4} w={"70%"} pr="16px">
          <Text fontSize="14px" color="#747474">
            {t('settingsIntegration.addGoogleMeetLocation') || "Add Google Meet as a location option for your meeting and events with myBridge's Google Meet integration."}
          </Text>
          <Text fontSize="12px" color="#747474">
            {t('settingsIntegration.googleCalendarAlreadyConnected') || "If you have a Google Calendar connected to your myBridge account, the Google Meet integration is already active."}
          </Text>
          <HStack spacing={4} w="100%">
            <VStack alignItems="flex-start">
              <GoogleMeetSuggestion />
              <Text fontSize="10px" color="#747474">
                {t('settingsIntegration.googleMeetSelection') || "When you connect a Google Calendar to your myBridge account, Google Meet will appear as an option when you select a meeting location."}
              </Text>
            </VStack>
          </HStack>
        </VStack>
        <Box
          w="27%"
          p="16px 12px"
          borderRadius="4px"
          boxShadow="0px 0px 12px 0px #00000033"
          border="1px solid #D9D9D9"
        >
          <Text fontSize="12px" fontWeight={500} color="#5B5B5B">
            {t('settingsIntegration.integrationRequirement') || "For this integration, you need Google Calendar connected with your myBridge account."}
          </Text>
          <Button
            mt={4}
            p="2px 8px"
            borderRadius="30px"
            h="29px"
            bgColor="#3D5A80"
            fontSize="11px"
            fontWeight={600}
            color="#FFFFFF"
            w="100%"
            onClick={() => mutate()}
          >
            {t('settingsIntegration.connectGoogleCalendar') || "Connect Google Calendar"}
          </Button>
        </Box>
      </HStack>
    </VStack>
      )}

      <Modal
        isOpen={isConfirmRemoveModalOpen?.isOpen}
        onClose={() => setIsConfirmRemoveModalOpen({ isOpen: false })}
        isCentered
      >
        <ModalOverlay />
        <ModalContent w={350} h={107}>
          <ModalCloseButton />
          <ModalBody>
            <Stack mb={2}>
              <Text fontSize={16} color="#5B5B5B">
                {t('settingsIntegration.disconnectGoogle') || "Disconnect Google?"}
              </Text>
            </Stack>
            <ModalFooter pr={0} pt={4}>
              <Button
                borderRadius={'full'}
                fontSize={16}
                variant="ghost"
                onClick={() => setIsConfirmRemoveModalOpen({ isOpen: false })}
              >
                {t('myCalendar.dismiss') || "Dismiss"}
              </Button>
              <Button
                variant={'primary'}
                borderRadius={'full'}
                fontSize={15}
                onClick={() => handleRemoveCalendar(selectedCalendars?.[0])}
                // backgroundColor="#E4E6EB"
              >
                {t('myCalendar.confirm') || "Confirm"}
              </Button>
            </ModalFooter>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default GoogleMeetIntegration;
